import { Home, Monitor } from 'lucide-react';

type TuitionMode = 'physical' | 'online' | 'both';

interface TuitionModeSelectorProps {
  value: string;
  onChange: (value: TuitionMode | '') => void;
  labels?: {
    physical: string;
    online: string;
    both: string;
    selected: string;
    physicalHint?: string;
  };
}

const TuitionModeSelector = ({ value, onChange, labels = { physical: 'Physical Tuition', online: 'Online Tuition', both: 'Both', selected: 'Selected' } }: TuitionModeSelectorProps) => {
  const togglePhysical = () => onChange(value === 'physical' ? '' : value === 'both' ? 'online' : 'physical');
  const toggleOnline = () => onChange(value === 'online' ? '' : value === 'both' ? 'physical' : 'online');
  const toggleBoth = () => onChange(value === 'both' ? '' : 'both');

  const buttonClass = (selected: boolean) => `flex h-full w-full cursor-pointer flex-col items-center justify-center space-y-3 rounded-xl px-4 py-5 font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
    selected
      ? 'border-2 border-primary bg-primary-50 text-primary shadow-sm'
      : 'border border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:bg-primary-50/50'
  }`;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div className="relative min-w-0">
        <button type="button" onClick={togglePhysical} aria-pressed={value === 'physical' || value === 'both'} className={buttonClass(value === 'physical' || value === 'both')}>
          <Home className="h-5 w-5" />
          <span className="text-sm">{labels.physical}</span>
          {(value === 'physical' || value === 'both') && <span className="text-xs font-semibold">✓ {labels.selected}</span>}
        </button>
        {labels.physicalHint && <span role="note" className="pointer-events-none absolute right-2 top-0 z-10 -translate-y-1/2 whitespace-nowrap rounded-lg border border-primary-200 bg-primary-50 px-2 py-1 text-center text-[11px] font-semibold leading-4 text-primary shadow-sm">{labels.physicalHint}</span>}
      </div>
      <div className="min-w-0">
        <button type="button" onClick={toggleOnline} aria-pressed={value === 'online' || value === 'both'} className={buttonClass(value === 'online' || value === 'both')}>
          <Monitor className="h-5 w-5" />
          <span className="text-sm">{labels.online}</span>
          {(value === 'online' || value === 'both') && <span className="text-xs font-semibold">✓ {labels.selected}</span>}
        </button>
      </div>
      <div className="min-w-0">
        <button type="button" onClick={toggleBoth} aria-pressed={value === 'both'} className={buttonClass(value === 'both')}>
          <div className="flex space-x-2"><Home className="h-5 w-5" /><Monitor className="h-5 w-5" /></div>
          <span className="text-sm">{labels.both}</span>
          {value === 'both' && <span className="text-xs font-semibold">✓ {labels.selected}</span>}
        </button>
      </div>
    </div>
  );
};

export default TuitionModeSelector;
